import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatLakh, timeAgo, MONEY_UNIT_SHORT } from "@/lib/utils-ros";
import { toast } from "sonner";
import { Sparkles, ChevronRight, ChevronDown, Loader2, Layers, Trash2, Upload, Download, X, AlertTriangle, CheckCircle2, Filter, Package } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ActionEditor } from "@/components/revenueos/ActionEditor";
import { EditableField } from "@/components/revenueos/EditableField";
import { MeetingLogInline } from "@/components/revenueos/MeetingLog";
import { PartnerAssignmentPanel } from "@/components/revenueos/PartnerAssignment";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { CustomerCard } from "@/components/revenueos/CustomerCard";
import { useAuth } from "@/lib/auth.jsx";

const URGENCY_COLORS = {
  urgent: "var(--danger)", high: "var(--copper)", medium: "var(--warning)", low: "var(--info)"
};
const URGENCY_ORDER = ["urgent", "high", "medium", "low"];
const CONTACT_ROLES = [
  ["unknown", "Role not set"], ["economic_buyer", "Economic Buyer"],
  ["power_sponsor", "Power Sponsor"], ["champion", "Champion"],
  ["technical_evaluator", "Technical Evaluator"], ["operations", "Operations"],
  ["finance", "Finance"], ["procurement", "Procurement"], ["it", "IT"],
  ["legal", "Legal"], ["influencer", "Influencer"], ["user", "User"],
];

function QualificationBar({ value }) {
  const score = Number(value) || 0;
  const color = score >= 80 ? "#16A34A" : score >= 60 ? "#E28A23" : "#DC2626";
  return (
    <div className="flex items-center justify-center gap-2 w-28">
      <div className="flex-1 h-1.5 bg-black/[0.06] rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, background: color }} />
      </div>
      <span className="text-[12px] tabular-nums text-[#526D7A] w-6 text-right">{value ?? "—"}</span>
    </div>
  );
}

export default function Leads() {
  const { canWrite } = useAuth();
  const canEditLeads = canWrite("leads");
  const canLogMeetings = canWrite("customers");
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [converting, setConverting] = useState(null);
  const [query, setQuery] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState(new Set()); // empty = all
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | converted
  const [minScore, setMinScore] = useState(0);
  const [industryFilter, setIndustryFilter] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [customerCid, setCustomerCid] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const nav = useNavigate();

  async function load(opts = {}) {
    const silent = opts?.silent === true;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch {
      if (!silent) toast.error("Could not load leads");
      setLeads([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setQuery(q);
  }, [searchParams]);

  async function convert(id) {
    setConverting(id);
    try {
      const { data } = await api.post(`/leads/${id}/convert`);
      toast.success("Lead converted to opportunity");
      nav(`/opportunities/${data.opportunity_id}`);
    } catch { toast.error("Conversion failed"); }
    finally { setConverting(null); }
  }

  async function deleteLead(lid, name) {
    if (!window.confirm(`Delete this lead for ${name || "this company"}?\n\nIt will be moved to Lost. You can restore it using the Lost button on the right side of the pipeline bar.`)) return;
    try {
      await api.delete(`/leads/${lid}?reason=user_deleted`);
      toast.success("Moved to Lost · restore from the pipeline bar");
      setLeads(s => s.filter(x => x.id !== lid));
    } catch { toast.error("Delete failed"); }
  }

  function toggleUrgency(u) {
    setUrgencyFilter(prev => {
      const next = new Set(prev);
      if (next.has(u)) next.delete(u); else next.add(u);
      return next;
    });
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter(l => {
      if (statusFilter === "open" && l.status === "converted") return false;
      if (statusFilter === "converted" && l.status !== "converted") return false;
      if (urgencyFilter.size && !urgencyFilter.has(l.urgency)) return false;
      if (industryFilter !== "all" && l.company?.industry !== industryFilter) return false;
      if (minScore > 0 && (l.ai_qualification_score ?? 0) < minScore) return false;
      if (q) {
        const hay = [
          l.company?.name, l.contact_name, l.contact_title, l.pain_point,
          l.company?.industry, ...(l.product_interest || []),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leads, query, statusFilter, urgencyFilter, industryFilter, minScore]);

  const openCount = leads.filter(l => l.status !== "converted").length;
  const convertedCount = leads.filter(l => l.status === "converted").length;
  const industries = [...new Set(leads.map(l => l.company?.industry).filter(Boolean))].sort();
  const activeFilterCount = [statusFilter !== "all", urgencyFilter.size > 0, industryFilter !== "all", minScore > 0].filter(Boolean).length;

  function clearFilters() {
    setStatusFilter("all");
    setUrgencyFilter(new Set());
    setIndustryFilter("all");
    setMinScore(0);
  }

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="leads-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Leads</h1>
        <p className="text-[13px] text-white/75 mt-1">Qualified customer interest, prioritized and ready to turn into opportunities.</p>
      </section>

      {importOpen && (
        <ImportLeadsModal
          onClose={() => setImportOpen(false)}
          onImported={async () => { setImportOpen(false); await load(); }}
        />
      )}

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search leads by company, contact, pain point, or product…"
          entityLabel="leads"
          count={filtered.length}
          variant="universe"
          right={(
            <div className="flex items-center justify-end gap-2 flex-wrap shrink-0">
              {canEditLeads && (
              <button
                onClick={() => setImportOpen(true)}
                data-testid="import-leads"
                className="h-9 px-3 rounded-lg border border-[#BFD6E1] bg-white text-[#07539A] hover:border-[#00A2D4] text-[12px] font-medium flex items-center gap-1.5 shadow-[0_4px_12px_rgba(7,83,154,0.06)]"
              >
                <Upload size={13} /> Import leads
              </button>
              )}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setFilterOpen(open => !open)}
                  data-testid="leads-filter-btn"
                  className={`h-9 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-2 shadow-[0_4px_12px_rgba(7,83,154,0.06)] transition-colors ${filterOpen || activeFilterCount ? "border-[#07539A] bg-[#E3F2F8] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#243B53] hover:border-[#07539A]"}`}
                >
                  <Filter size={14} /> Filter
                  {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
                </button>
                {filterOpen && (
                  <div className="absolute right-0 top-11 z-30 w-[min(92vw,34rem)] rounded-2xl border border-[#C9DCE6] bg-white shadow-[0_20px_50px_rgba(7,83,154,0.20)] p-4" data-testid="leads-filter-panel">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="font-display text-lg text-[#07539A]">Filter leads</div>
                        <div className="text-[10px] text-[#78909C] mt-0.5">Combine filters to focus the qualification queue.</div>
                      </div>
                      <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" className="w-8 h-8 rounded-full hover:bg-[#EDF7FB] text-[#607D8B] flex items-center justify-center"><X size={14} /></button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label className="block">
                        <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Lead status</span>
                        <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)} data-testid="leads-status-filter" className="w-full h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]">
                          <option value="all">All leads ({leads.length})</option>
                          <option value="open">Open ({openCount})</option>
                          <option value="converted">Converted ({convertedCount})</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Industry</span>
                        <select value={industryFilter} onChange={event => setIndustryFilter(event.target.value)} data-testid="leads-industry-filter" className="w-full h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]">
                          <option value="all">All industries</option>
                          {industries.map(industry => <option key={industry} value={industry}>{industry}</option>)}
                        </select>
                      </label>
                    </div>

                    <div className="mt-4">
                      <div className="text-[11px] font-semibold text-[#455A64] mb-2">Urgency</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {URGENCY_ORDER.map(u => {
                          const active = urgencyFilter.has(u);
                          const count = leads.filter(l => l.urgency === u).length;
                          return (
                            <button key={u} type="button" onClick={() => toggleUrgency(u)} data-testid={`leads-urgency-${u}`} className="text-[10px] capitalize px-2.5 h-7 rounded-full border font-medium transition-all" style={active ? { background: URGENCY_COLORS[u], borderColor: URGENCY_COLORS[u], color: "white" } : { borderColor: URGENCY_COLORS[u], color: URGENCY_COLORS[u], background: "transparent" }}>
                              {u} · {count}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-4 rounded-xl bg-[#F8FCFE] border border-[#D7E8F0] p-3 flex items-center gap-3">
                      <span className="text-[11px] font-semibold text-[#455A64] shrink-0">Minimum lead strength</span>
                      <input type="range" min={0} max={100} step={5} value={minScore} onChange={event => setMinScore(Number(event.target.value))} data-testid="leads-score-slider" className="accent-[#07539A] flex-1" />
                      <span className="min-w-8 h-7 px-2 rounded-full bg-[#E7F5FB] text-[#07539A] text-[11px] font-semibold flex items-center justify-center" data-testid="leads-score-value">{minScore}</span>
                    </div>

                    <div className="mt-4 pt-3 border-t border-[#E1EBF0] flex items-center justify-between">
                      <button type="button" onClick={clearFilters} className="text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear all</button>
                      <button type="button" onClick={() => setFilterOpen(false)} className="h-9 px-4 rounded-xl bg-[#07539A] !text-white text-[12px] font-medium">Show {filtered.length} leads</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        />

      <div className="border border-[#C9DCE6] rounded-[22px] overflow-x-auto bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="leads-table-scroll">
        <div className="grid min-w-[1030px] grid-cols-[minmax(160px,1.1fr)_minmax(260px,1.6fr)_minmax(150px,0.9fr)_90px_140px_100px_18px] gap-3 items-center px-4 py-3 bg-[#F3FAFD] border-b border-[#D7E8F0] text-[11px] font-semibold text-[#07539A]">
          <div>Company</div>
          <div>Lead intent</div>
          <div className="text-center">Contact</div>
          <div className="text-center">Urgency</div>
          <div className="text-center">Lead strength</div>
          <div className="text-center">Est. value ({MONEY_UNIT_SHORT})</div>
          <div />
        </div>
        {loading ? (
          <div className="p-8 text-center text-[var(--text-muted)]">Loading leads…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[var(--text-muted)] text-[13px]">
            {leads.length === 0 ? "No leads yet — try Quick Add above." : "No leads match your filters. Try relaxing them."}
          </div>
        ) : (
          <div className="min-w-[1030px] divide-y divide-black/10">
            {filtered.map(l => {
              const isExp = expanded === l.id;
              return (
                <div key={l.id} data-testid={`lead-row-${l.id}`}>
                  <button
                    onClick={() => setExpanded(isExp ? null : l.id)}
                    data-testid={`lead-expand-${l.id}`}
                    className="w-full text-left grid grid-cols-[minmax(160px,1.1fr)_minmax(260px,1.6fr)_minmax(150px,0.9fr)_90px_140px_100px_18px] gap-3 items-center px-4 py-4 hover:bg-[#F8FCFE] transition-colors"
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-sm bg-black/[0.05] border border-black/10 flex items-center justify-center text-[12px] text-[#526D7A] shrink-0">
                        {(l.company?.name || "?").substring(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[12px] font-medium text-[#455A64] truncate">{l.company?.name || "Unknown company"}</div>
                        <div className="text-[10px] text-[#78909C] truncate mt-0.5">{l.company?.industry || "—"}</div>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12px] text-[#455A64] line-clamp-2">{l.pain_point || "No intent captured"}</div>
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#78909C]">
                        <span>{timeAgo(l.created_at)}</span>
                        {l.ai_next_actions?.length > 0 && l.status !== "converted" && <span>· {l.ai_next_actions.length} action{l.ai_next_actions.length === 1 ? "" : "s"}</span>}
                      </div>
                    </div>
                    <div className="min-w-0 text-center">
                      <div className="text-[12px] text-[#455A64] truncate">{l.contact_name || "Unassigned"}</div>
                      <div className="text-[10px] text-[#78909C] truncate mt-0.5">{l.contact_title || "No title"}</div>
                    </div>
                    <div className="flex justify-center">
                      <span className="text-[10px] capitalize px-2.5 h-7 rounded-full border font-medium inline-flex items-center bg-[#F8FCFE]" style={{ borderColor: URGENCY_COLORS[l.urgency], color: URGENCY_COLORS[l.urgency] }}>
                        {l.urgency}
                      </span>
                    </div>
                    <div className="flex justify-center"><QualificationBar value={l.ai_qualification_score} /></div>
                    <div className="text-center text-[12px] text-[#526D7A] tabular-nums">{formatLakh(l.estimated_value_cr)}</div>
                    <div className="flex items-center justify-center text-[#78909C]">
                      {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </div>
                  </button>

                  {isExp && (
                    <div className="sticky left-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] md:static md:w-auto md:max-w-none md:mx-2 mb-3 px-3 md:px-4 py-4 bg-[#EAF7FC] border border-[#BBD8E7] rounded-[22px] space-y-4 shadow-[0_10px_24px_rgba(7,83,154,0.07)]" data-testid={`lead-details-${l.id}`}>
                      <button
                        onClick={() => setCustomerCid(l.company_id)}
                        data-testid={`customer-360-${l.id}`}
                        className="w-full min-h-11 px-4 rounded-[16px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[12px] font-medium flex items-center justify-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]"
                      >
                        <Layers size={12} /> Open Customer 360 — {l.company?.name} · All history across stages
                      </button>
                      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.06)]">
                        <EditableField
                          label={<span className="flex items-center gap-2"><Package size={13} /> Interested products</span>}
                          value={(l.product_interest || []).join(", ")}
                          placeholder="Add products, separated by commas…"
                          testId={`lead-products-${l.id}`}
                          readOnly={!canEditLeads}
                          className="border-0 rounded-none bg-transparent p-3 md:p-4"
                          labelClassName="text-[11px] uppercase tracking-widest text-[#07539A]"
                          onSave={async (value) => {
                            const products = value.split(",").map(product => product.trim()).filter(Boolean);
                            await api.patch(`/leads/${l.id}`, { product_interest: products });
                            setLeads(prev => prev.map(item => item.id === l.id ? { ...item, product_interest: products } : item));
                            toast.success("Interested products updated");
                          }}
                        />
                      </div>

                      <div className="grid md:grid-cols-3 gap-3">
                        <EditableField
                          label="Primary Lead Contact"
                          value={l.contact_name}
                          testId={`lead-contact-name-${l.id}`}
                          readOnly={!canEditLeads}
                          className="rounded-[18px] border-[#C9DCE6] bg-white shadow-[0_6px_18px_rgba(7,83,154,0.05)]"
                          onSave={async (v) => {
                            await api.patch(`/leads/${l.id}`, { contact_name: v.trim() });
                            setLeads((prev) => prev.map((x) => x.id === l.id ? { ...x, contact_name: v.trim() } : x));
                            toast.success("Primary lead contact updated");
                          }}
                        />
                        <EditableField
                          label="Primary Contact Title"
                          value={l.contact_title}
                          testId={`lead-contact-title-${l.id}`}
                          readOnly={!canEditLeads}
                          className="rounded-[18px] border-[#C9DCE6] bg-white shadow-[0_6px_18px_rgba(7,83,154,0.05)]"
                          onSave={async (v) => {
                            await api.patch(`/leads/${l.id}`, { contact_title: v.trim() });
                            setLeads((prev) => prev.map((x) => x.id === l.id ? { ...x, contact_title: v.trim() } : x));
                            toast.success("Primary contact title updated");
                          }}
                        />
                        <label className="rounded-[18px] border border-[#C9DCE6] bg-white px-3 py-2 shadow-[0_6px_18px_rgba(7,83,154,0.05)]">
                          <span className="block text-[10px] uppercase tracking-widest text-[var(--text-dim)] mb-1.5">Buying role</span>
                          <select
                            value={l.contact_role || "unknown"}
                            disabled={!canEditLeads}
                            data-testid={`lead-contact-role-${l.id}`}
                            onChange={async (event) => {
                              const contact_role = event.target.value;
                              await api.patch(`/leads/${l.id}`, { contact_role });
                              setLeads(prev => prev.map(item => item.id === l.id ? { ...item, contact_role } : item));
                              toast.success("Lead contact role updated");
                            }}
                            className="w-full h-8 bg-transparent outline-none text-[13px] text-[#455A64]"
                          >
                            {CONTACT_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </label>
                      </div>
                      <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.06)]">
                        <EditableField
                          label={<span className="flex items-center gap-2"><AlertTriangle size={13} /> Pain point / intent</span>}
                          value={l.pain_point}
                          testId={`lead-pain-point-${l.id}`}
                          multiline
                          readOnly={!canEditLeads}
                          className="border-0 rounded-none bg-transparent p-3 md:p-4"
                          labelClassName="text-[11px] uppercase tracking-widest text-[#07539A]"
                          inputClassName="leading-relaxed"
                          onSave={async (v) => {
                            await api.patch(`/leads/${l.id}`, { pain_point: v.trim() });
                            setLeads((prev) => prev.map((x) => x.id === l.id ? { ...x, pain_point: v.trim() } : x));
                            toast.success("Pain point updated");
                          }}
                        />
                      </div>
                      <ActionEditor
                        actions={l.ai_next_actions || []}
                        aiInsights={l.ai_summary}
                        title="Actions to take"
                        accent="navy"
                        rounded
                        readOnly={!canEditLeads}
                        onSave={async (arr) => {
                          const { data } = await api.patch(`/leads/${l.id}`, { ai_next_actions: arr });
                          setLeads(prev => prev.map(x => x.id === l.id ? { ...x, ai_next_actions: arr } : x));
                          return data;
                        }}
                        onRegenerate={async () => {
                          const { data } = await api.post(`/leads/${l.id}/regenerate-ai`);
                          if (data.ai_summary) setLeads(prev => prev.map(x => x.id === l.id ? { ...x, ai_summary: data.ai_summary } : x));
                          return data;
                        }}
                      />

                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='partner-assignment']]:border-0">
                          <PartnerAssignmentPanel entityType="lead" entityId={l.id} accent="navy" />
                        </div>
                        <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='meeting-log']]:border-0">
                          <MeetingLogInline entityType="lead" entityId={l.id} accent="navy" readOnly={!canLogMeetings} />
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-1">
                        {l.promoted_opportunity_id ? (
                          <button
                            onClick={() => nav(`/opportunities/${l.promoted_opportunity_id}`)}
                            data-testid={`view-opp-${l.id}`}
                            className="h-9 px-4 rounded-xl bg-[var(--success)]/10 border border-[var(--success)]/40 text-[var(--success)] text-[12px] font-medium flex items-center gap-2 hover:bg-[var(--success)]/15"
                          >
                            ✓ Converted · View Opportunity →
                          </button>
                        ) : l.status !== "converted" && canEditLeads && (
                          <button
                            onClick={() => convert(l.id)}
                            disabled={converting === l.id}
                            data-testid={`convert-lead-${l.id}`}
                            className="h-9 px-4 rounded-xl bg-[#E28A23] hover:bg-[#C97215] !text-white text-[12px] font-medium flex items-center gap-2 disabled:opacity-50 shadow-[0_8px_20px_rgba(226,138,35,0.24)]"
                          >
                            {converting === l.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            Convert to Opportunity
                          </button>
                        )}
                        {canEditLeads && (
                        <button
                          onClick={() => deleteLead(l.id, l.company?.name)}
                          data-testid={`delete-lead-${l.id}`}
                          className="text-[11px] px-3 h-8 rounded-xl bg-white border border-[var(--danger)]/30 text-[var(--danger)] hover:bg-[var(--danger)]/5 flex items-center gap-1"
                        >
                          <Trash2 size={11} /> Delete lead
                        </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
      {customerCid && <CustomerCard companyId={customerCid} onClose={() => setCustomerCid(null)} />}
    </div>
  );
}

/** Template-based CSV lead import.
 *
 * Two-step by design: the file is always validated server-side first
 * (`dry_run`) and the result shown, so nothing is written until the user has
 * seen exactly what would happen. The backend refuses a partial import — if any
 * row has an error, nothing lands.
 */
function ImportLeadsModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [createCompanies, setCreateCompanies] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  async function downloadTemplate() {
    try {
      // Fetched via axios rather than a plain link so the Authorization header
      // is attached; the browser then downloads the blob.
      const res = await api.get("/leads/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "saarvos-leads-template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the template");
    }
  }

  async function send(dryRun) {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dryRun ? "true" : "false");
      fd.append("create_missing_companies", createCompanies ? "true" : "false");
      const { data } = await api.post("/leads/import", fd);
      setPreview(data);
      if (!dryRun && data.summary?.imported > 0) {
        toast.success(`Imported ${data.summary.imported} lead${data.summary.imported === 1 ? "" : "s"}`);
        await onImported();
      } else if (!dryRun) {
        toast.error(data.summary?.aborted || "Nothing was imported");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  }

  // Re-validating is required after changing the file or the company option —
  // an old preview would not describe what is about to be imported.
  function pickFile(f) { setFile(f); setPreview(null); }

  const s = preview?.summary;
  const badRows = (preview?.rows || []).filter(r => r.errors?.length);
  const canImport = s && !s.invalid && s.valid > 0 && s.dry_run;

  return (
    <div className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-start justify-center pt-12 px-4" onClick={onClose}>
      <div className="bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="import-leads-modal">
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]"><Upload size={15} /></div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">Import Leads</div>
          <button onClick={onClose} data-testid="import-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A]"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <div className="flex items-start gap-3 p-4 rounded-[16px] bg-white border border-[#C9DCE6] shadow-[0_4px_14px_rgba(7,83,154,0.04)]">
            <div className="flex-1">
              <div className="text-[13px] font-semibold text-[#07539A]">1 · Start from the template</div>
              <p className="text-[11px] text-[#607D8B] mt-1 leading-relaxed">
                CSV with the expected columns and two example rows. Only <span className="font-mono">company_name</span> is required.
              </p>
            </div>
            <button
              onClick={downloadTemplate} data-testid="download-template"
              className="h-9 px-3 rounded-[12px] border border-[#BBD3DF] bg-[#F8FCFE] hover:border-[#07539A] text-[#07539A] text-[11px] flex items-center gap-1.5 shrink-0 transition-colors"
            >
              <Download size={12} /> Template
            </button>
          </div>

          <div className="p-4 rounded-[16px] bg-white border border-[#C9DCE6] shadow-[0_4px_14px_rgba(7,83,154,0.04)]">
            <div className="text-[13px] font-semibold text-[#07539A] mb-2">2 · Upload the filled file</div>
            <input
              type="file" accept=".csv,text/csv" data-testid="import-file"
              onChange={e => pickFile(e.target.files?.[0] || null)}
              className="w-full rounded-[10px] border border-[#D7E8F0] bg-[#F8FCFE] p-2 text-[12px] text-[#607D8B] file:mr-3 file:h-8 file:px-3 file:rounded-lg file:border file:border-[#BBD3DF] file:bg-white file:text-[#07539A] file:text-[11px] file:cursor-pointer"
            />
            <label className="flex items-center gap-2 mt-3 text-[11px] text-[#607D8B] cursor-pointer">
              <input
                type="checkbox" checked={createCompanies} data-testid="import-create-companies"
                onChange={e => { setCreateCompanies(e.target.checked); setPreview(null); }}
              />
              Create companies that aren't in Universe yet
            </label>
          </div>

          {s && (
            <div data-testid="import-preview" className="space-y-3 p-4 rounded-[16px] bg-white border border-[#C9DCE6] shadow-[0_4px_14px_rgba(7,83,154,0.04)]">
              <div className="text-[13px] font-semibold text-[#07539A]">3 · Review</div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <span className="px-2.5 py-1 rounded-full bg-[#F3FAFD] border border-[#C9DCE6]">{s.total_rows} rows</span>
                <span className="px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--success)", color: "var(--success)" }}>{s.valid} valid</span>
                {s.invalid > 0 && (
                  <span className="px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--danger)", color: "var(--danger)" }}>{s.invalid} with errors</span>
                )}
                {s.companies_to_create > 0 && (
                  <span className="px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--warning)", color: "var(--warning)" }}>{s.companies_to_create} new companies</span>
                )}
              </div>

              {s.unknown_columns?.length > 0 && (
                <p className="text-[11px] text-[var(--warning)] flex items-start gap-1.5">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  Ignored unknown column(s): {s.unknown_columns.join(", ")}
                </p>
              )}

              {badRows.length > 0 ? (
                <div className="border border-[#D7E8F0] rounded-[12px] divide-y divide-[#E1EBF0] max-h-56 overflow-y-auto bg-[#F8FCFE]">
                  {badRows.map(r => (
                    <div key={r.row} className="p-2 text-[11px] flex gap-2">
                      <span className="font-mono text-[var(--text-dim)] shrink-0">row {r.row}</span>
                      <span className="flex-1">
                        <span className="text-[var(--text-muted)]">{r.company_name || "(no company)"}</span>
                        <ul className="mt-0.5 space-y-0.5">
                          {r.errors.map((e, i) => (
                            <li key={i} style={{ color: "var(--danger)" }}>· {e}</li>
                          ))}
                        </ul>
                      </span>
                    </div>
                  ))}
                </div>
              ) : s.dry_run ? (
                <p className="text-[11px] flex items-center gap-1.5" style={{ color: "var(--success)" }}>
                  <CheckCircle2 size={12} /> Everything checks out — ready to import.
                </p>
              ) : null}

              {s.invalid > 0 && (
                <p className="text-[11px] text-[var(--text-muted)]">
                  Nothing is imported while any row has an error. Fix the rows above and upload again.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#C9DCE6] bg-[#E7F5FB] flex gap-3 justify-end">
          <button
            onClick={() => send(true)} disabled={busy || !file} data-testid="import-validate"
            className="h-10 px-4 rounded-[12px] border border-[#BBD3DF] bg-white text-[#07539A] hover:border-[#07539A] disabled:opacity-50 text-[12px] flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : null} Validate
          </button>
          <button
            onClick={() => send(false)} disabled={busy || !canImport} data-testid="import-commit"
            className="h-10 px-5 rounded-[12px] bg-[#07539A] hover:bg-[#063F73] disabled:opacity-50 !text-white text-[12px] font-medium flex items-center gap-1.5 shadow-[0_7px_18px_rgba(7,83,154,0.16)]"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Import {s?.valid ? `${s.valid} lead${s.valid === 1 ? "" : "s"}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
